import type {TemplateNode} from "#template/template-node/template-node"
import type {TransformValue} from "#template/value-types"
import type {INodeSceneInteractionsContext, TransformAccessor, TemplateNodePart, TransformInteraction} from "#template/interactions"
import {isTransformValue} from "#template/value-types"
import {TransformMode} from "#template/interactions"
import type {PositionValue, TransformLike} from "#template/nodes/types"
import {cameraLookAt, targetFromTransform} from "#template/utils/camera-utils"
import {Matrix4, Vector3} from "@cm/math"

interface ITransformAccessorProvider {
    getTransformAccessor(templateNode: TemplateNode): TransformAccessor | undefined
}

type TargetableNode = TemplateNode<{lockedTransform?: TransformValue; target: PositionValue; targeted: boolean; focalDistance?: number}>

export function isNodeWithMatrixArrayLockedTransformParameter<P extends {lockedTransform?: TransformLike}>(
    node: TemplateNode<P>,
): node is TemplateNode<P & {lockedTransform: TransformValue}> {
    return isTransformValue(node.parameters.lockedTransform)
}

export function beginTargetableTransformInteraction(
    node: TargetableNode,
    part: TemplateNodePart["part"],
    context: INodeSceneInteractionsContext,
): TransformInteraction | undefined {
    const accessor = context.getTransformAccessor(node)
    const startWorld = accessor?.getTransform()
    if (!accessor || !startWorld) return undefined
    const targetAccessor = context.getLiveValueAccessor<Vector3>(node)

    const startProvider: ITransformAccessorProvider = {
        getTransformAccessor: (n) => (n === node ? ({getTransform: () => startWorld} as TransformAccessor) : context.getTransformAccessor(n)),
    }
    let lastTransform: Matrix4 | undefined
    return {
        update: (transform) => {
            lastTransform = transform
            const {transform: constrained, target} = constrainTransformTargetableNode(
                startProvider,
                transform,
                {templateNode: node, part},
                context.transformMode,
            )
            accessor.setTransform(constrained, true)
            if (target) targetAccessor?.setLiveValue(target)
        },
        commit: () => {
            accessor.endTransform()
            targetAccessor?.endInteraction()
            if (!lastTransform) return
            const {transform: constrained, target} = constrainTransformTargetableNode(
                startProvider,
                lastTransform,
                {templateNode: node, part},
                context.transformMode,
            )
            if (constrained) node.updateParameters({lockedTransform: constrained.toArray()})
            if (target) node.updateParameters({target: target.toArray()})
        },
        cancel: () => {
            accessor.setTransform(startWorld, true)
            accessor.endTransform()
            targetAccessor?.endInteraction()
        },
    }
}

export function constrainTransformTargetableNode(
    transformAccessorProvider: ITransformAccessorProvider,
    transform: Matrix4,
    transformedNodePart: {
        templateNode: TargetableNode
        part: TemplateNodePart["part"]
    },
    mode: TransformMode,
): {transform: Matrix4; target?: Vector3} {
    const {templateNode, part} = transformedNodePart

    if (!templateNode.parameters.targeted) {
        if (part !== "root") throw new Error("Part must be root")

        const {focalDistance} = templateNode.parameters
        if (focalDistance !== undefined) {
            return {transform, target: targetFromTransform(transform, focalDistance)}
        }

        const {target: targetV} = templateNode.parameters

        const delta = new Matrix4(getTransformDelta(transformAccessorProvider, transform, templateNode)?.toArray())
        if (!delta) throw new Error("Delta is undefined")

        const curTarget = new Vector3(targetV[0], targetV[1], targetV[2])
        const newTargetH = delta.multiplyVectorXYZW(curTarget.x, curTarget.y, curTarget.z, 1)
        const newTarget = new Vector3(newTargetH[0] / newTargetH[3], newTargetH[1] / newTargetH[3], newTargetH[2] / newTargetH[3])

        return {
            transform,
            target: newTarget,
        }
    }

    if (part !== "root" && part !== "target") throw new Error("Part must be root or target")

    if (mode === TransformMode.Translate) {
        if (part === "target") {
            const newTarget = transform.getTranslation()
            if (!(newTarget instanceof Vector3)) throw new Error("newTarget is not a Vector3")
            const newTransform = cameraLookAt(new Matrix4(templateNode.parameters.lockedTransform).getTranslation(), newTarget)
            return {transform: newTransform, target: newTarget}
        } else {
            const {target: targetV} = templateNode.parameters
            const target = new Vector3(targetV[0], targetV[1], targetV[2])
            const newPosition = transform.getTranslation()
            if (!(newPosition instanceof Vector3)) throw new Error("newPosition is not a Vector3")
            const newTransform = cameraLookAt(newPosition, target)
            return {transform: newTransform}
        }
    } else if (mode === TransformMode.Rotate) {
        if (part === "target") {
            throw new Error("Rotating around a target is currently not supported")
        } else {
            const {target: targetV} = templateNode.parameters
            const delta = new Matrix4(getTransformDelta(transformAccessorProvider, transform, templateNode)?.toArray())
            if (!delta) throw new Error("Delta is undefined")

            const curTarget = new Vector3(targetV[0], targetV[1], targetV[2])
            const newTargetH = delta.multiplyVectorXYZW(curTarget.x, curTarget.y, curTarget.z, 1)
            const newTarget = new Vector3(newTargetH[0] / newTargetH[3], newTargetH[1] / newTargetH[3], newTargetH[2] / newTargetH[3])

            const newTranslation = transform.getTranslation()

            const newTransform = cameraLookAt(new Vector3(newTranslation.x, newTranslation.y, newTranslation.z), newTarget)
            return {transform: newTransform, target: newTarget}
        }
    } else {
        throw new Error("Scaling a camera or area light is currently not supported")
    }
}

function getTransformDelta(transformAccessorProvider: ITransformAccessorProvider, transform: Matrix4, templateNode: TemplateNode) {
    const sceneNodeMatrix = transformAccessorProvider.getTransformAccessor(templateNode)?.getTransform()
    if (!sceneNodeMatrix) return undefined
    const delta = transform.multiply(sceneNodeMatrix.inverse())
    return delta
}
